import React, { forwardRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { COLORS_ADMIN_PAGE, COLORS_CONFIG_PAGE } from '../../conf/constants';
import { ORDERS_TABLE_CONSTANTS } from '../constants/ordersTable';
import { User } from '../../user/types/type';
import { Order } from '../types/Order';
import OrdersTable from './OrdersTable';
import OrdersPagination from './OrdersPagination';

interface OrdersContentProps {
  selectedUser: User | null;
  orders: Order[];
  orderSearchTerm: string;
  setOrderSearchTerm: (term: string) => void;
  currentOrderPage: number;
  totalOrders: number;
  ordersPerPage: number;
  onOrderPageChange: (page: number) => void;
  onOrdersPerPageChange?: (ordersPerPage: number) => void; 
}

const OrdersContent = forwardRef<HTMLDivElement, OrdersContentProps>(({
  selectedUser,
  orders,
  orderSearchTerm,
  setOrderSearchTerm,
  currentOrderPage,
  totalOrders,
  ordersPerPage,
  onOrderPageChange,
  onOrdersPerPageChange,
}, ref) => {
  if (!selectedUser) {
    return (
      <Box sx={{ 
        p: 3, 
        textAlign: 'center', 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Typography variant="h6" color="textSecondary">
          {ORDERS_TABLE_CONSTANTS.NO_USER_SELECTED_MESSAGE}
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search Bar */}
      <Box sx={{ 
        p: 3, 
        borderBottom: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
        height: ORDERS_TABLE_CONSTANTS.SEARCH_BAR_HEIGHT,
        boxSizing: 'border-box',
        flexShrink: 0
      }}>
        <TextField
          size="small"
          placeholder={ORDERS_TABLE_CONSTANTS.SEARCH_PLACEHOLDER}
          variant="outlined"
          value={orderSearchTerm}
          onChange={(e) => setOrderSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 300,
            bgcolor: COLORS_CONFIG_PAGE.background,
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
            },
            borderRadius: 3,
          }}
        />
      </Box>

      {/* Orders Table */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden', 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0 // Important: allows flex item to shrink
      }}>
        <OrdersTable 
          orders={orders} 
          onOrdersPerPageChange={onOrdersPerPageChange}
        />
        <OrdersPagination
          currentPage={currentOrderPage}
          totalItems={totalOrders}
          itemsPerPage={ordersPerPage}
          onPageChange={onOrderPageChange}
        />
      </Box>
    </Box>
  );
});

OrdersContent.displayName = 'OrdersContent';

export default OrdersContent;